const { createAsyncThunk, createSlice } = require("@reduxjs/toolkit");

//initalsState
const initialState = {
  cartItems: [],
  loading: false,
  error: null,
  isAdded: false,
  isUpdated: false,
  isDelete: false,
};

//add product to cart - UPDATED with stock info
export const addOrderToCartaction = createAsyncThunk(
  "cart/add-to-cart",
  async (cartItem, { rejectWithValue }) => {
    try {
      const cartItems = localStorage.getItem("cartItems")
        ? JSON.parse(localStorage.getItem("cartItems"))
        : [];
      
      // Check if product already exists
      const existingItem = cartItems.find(
        (item) => item?.product?.toString() === cartItem?.product?.toString()
      );
      
      // Get stock information from the cartItem (passed from product page)
      const qtyLeft = cartItem.qtyLeft || (cartItem.totalQty - cartItem.totalSold) || 0;
      
      if (existingItem) {
        // Check if adding more exceeds stock
        const newQty = (existingItem.qty || 1) + (cartItem.qty || 1);
        if (newQty > qtyLeft) {
          return rejectWithValue(`Only ${qtyLeft} items available in stock`);
        }
        // If exists, increase quantity
        existingItem.qty = newQty;
        existingItem.totalPrice = existingItem.price * existingItem.qty;
      } else {
        // Add new item with stock information
        const newItem = {
          ...cartItem,
          _id: cartItem.product, // Use product ID as _id for consistency
          qty: cartItem.qty || 1,
          totalPrice: (cartItem.price || 0) * (cartItem.qty || 1),
          // STOCK INFORMATION - ADD THESE LINES
          totalQty: cartItem.totalQty,
          totalSold: cartItem.totalSold,
          qtyLeft: qtyLeft,
        };
        cartItems.push(newItem);
      }
      
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      return cartItems;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

//change item quantity - UPDATED with stock validation
export const changeOrderItemQty = createAsyncThunk(
  "cart/change-item-qty",
  async ({ productId, qty }, { rejectWithValue }) => {
    try {
      const cartItems = localStorage.getItem("cartItems")
        ? JSON.parse(localStorage.getItem("cartItems"))
        : [];
      
      const newCartItems = cartItems?.map((item) => {
        if (item?._id?.toString() === productId?.toString()) {
          // Check against available stock
          const availableStock = item.qtyLeft || item.totalQty || 999;
          
          if (qty > availableStock) {
            // This will be caught in the rejected case
            throw new Error(`Only ${availableStock} items available in stock`);
          }
          
          const newPrice = item?.price * qty;
          return {
            ...item,
            qty: +qty,
            totalPrice: newPrice,
          };
        }
        return item;
      });
      
      localStorage.setItem("cartItems", JSON.stringify(newCartItems));
      return newCartItems;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

//add product to cart
export const getCartItemsFromLocalStorageAction = createAsyncThunk(
  "cart/get-order-items",
  async () => {
    const cartItems = localStorage.getItem("cartItems")
      ? JSON.parse(localStorage.getItem("cartItems"))
      : [];
    return cartItems;
  }
);

//remove from cart
export const removeOrderItemQty = createAsyncThunk(
  "cart/removeOrderItem",
  async (productId) => {
    const cartItems = localStorage.getItem("cartItems")
      ? JSON.parse(localStorage.getItem("cartItems"))
      : [];
    
    const newItems = cartItems?.filter((item) => item?._id !== productId);
    localStorage.setItem("cartItems", JSON.stringify(newItems));
    return newItems; // IMPORTANT: Return the updated cart items
  }
);

//slice
const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    // Add a sync action for immediate updates
    clearCartState: (state) => {
      state.cartItems = [];
      state.isAdded = false;
      state.isUpdated = false;
      state.isDelete = false;
    },
  },
  extraReducers: (builder) => {
    //add to cart
    builder.addCase(addOrderToCartaction.pending, (state) => {
      state.loading = true;
      state.isAdded = false;
    });
    builder.addCase(addOrderToCartaction.fulfilled, (state, action) => {
      state.loading = false;
      state.cartItems = action.payload;
      state.isAdded = true;
      state.error = null;
    });
    builder.addCase(addOrderToCartaction.rejected, (state, action) => {
      state.loading = false;
      state.isAdded = false;
      state.error = action.error?.message || "Failed to add to cart";
    });
    
    //fetch cart items
    builder.addCase(getCartItemsFromLocalStorageAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCartItemsFromLocalStorageAction.fulfilled, (state, action) => {
      state.loading = false;
      state.cartItems = action.payload || [];
      state.error = null;
    });
    builder.addCase(getCartItemsFromLocalStorageAction.rejected, (state, action) => {
      state.loading = false;
      state.cartItems = [];
      state.error = action.error?.message || "Failed to load cart";
    });
    
    //change quantity
    builder.addCase(changeOrderItemQty.pending, (state) => {
      state.loading = true;
      state.isUpdated = false;
    });
    builder.addCase(changeOrderItemQty.fulfilled, (state, action) => {
      state.loading = false;
      state.cartItems = action.payload;
      state.isUpdated = true;
    });
    builder.addCase(changeOrderItemQty.rejected, (state, action) => {
      state.loading = false;
      state.isUpdated = false;
      state.error = action.error?.message;
    });
    
    //remove from cart
    builder.addCase(removeOrderItemQty.pending, (state) => {
      state.loading = true;
      state.isDelete = false;
    });
    builder.addCase(removeOrderItemQty.fulfilled, (state, action) => {
      state.loading = false;
      state.cartItems = action.payload;
      state.isDelete = true;
    });
    builder.addCase(removeOrderItemQty.rejected, (state, action) => {
      state.loading = false;
      state.isDelete = false;
      state.error = action.error?.message;
    });
  },
});

export const { clearCartState } = cartSlice.actions;
export default cartSlice.reducer;